import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Check, Flame, ArrowDown, RotateCcw, Trash2, Zap } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/* ════════════════════════════════════════════════════════════════
   Shared set-type catalogue + popover

   These are used by LiveSession, SetRow, ExerciseRow, and
   WorkoutEditDialog (and read-only by SocialPage). They live in a
   leaf module so neither LiveSession nor any of its inner row
   components have to import each other to access the set-type
   metadata or the picker popover — this breaks what was previously
   a SetRow → LiveSession → ExerciseRow → SetRow cycle (the build
   tolerated it via ES module live bindings, but it was a code
   smell). v3.8.178 cleanup (Task 8 of 8 of the WorkoutPage refactor).
   ════════════════════════════════════════════════════════════════ */
export const SET_TYPES = {
  work: { labelKey: 'workout.setType.work.label', label: 'Work Set', color: 'bg-primary', textColor: 'text-primary', icon: Dumbbell, descKey: 'workout.setType.work.desc', desc: 'Your main working sets at full intensity and target weight.' },
  warmup: { labelKey: 'workout.setType.warmup.label', label: 'Warm-up [W]', color: 'bg-amber-500', textColor: 'text-amber-500', icon: Flame, descKey: 'workout.setType.warmup.desc', desc: 'Lighter sets to prepare your muscles and joints before working sets.' },
  failure: { labelKey: 'workout.setType.failure.label', label: 'To Failure', color: 'bg-red-600', textColor: 'text-red-600', icon: Zap, descKey: 'workout.setType.failure.desc', desc: 'Push to complete muscular failure — no more reps possible with good form.' },
  drop: { labelKey: 'workout.setType.drop.label', label: 'Drop Set [D]', color: 'bg-teal-500', textColor: 'text-teal-500', icon: ArrowDown, descKey: 'workout.setType.drop.desc', desc: 'Reduce weight immediately after a set to failure and continue repping.' },
  backoff: { labelKey: 'workout.setType.backoff.label', label: 'Back-off [B]', color: 'bg-sky-500', textColor: 'text-sky-500', icon: RotateCcw, descKey: 'workout.setType.backoff.desc', desc: 'Lighter sets after your top sets to accumulate extra volume.' },
};

/* ── Set Type Selector Popover ───────────────────────────────── */
export function SetTypePopover({ currentType, onSelect, onDelete, children }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" side="bottom">
        <div className="p-3 border-b border-border">
          <p className="text-sm font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('workout.selectSetType')}</p>
        </div>
        <div className="p-1.5">
          {Object.entries(SET_TYPES).map(([key, st]) => {
            const Icon = st.icon;
            const active = key === currentType;
            return (
              <button
                key={key}
                data-testid={`set-type-${key}`}
                onClick={() => { onSelect(key); setOpen(false); }}
                className={`w-full flex items-start gap-3 p-2.5 rounded-lg text-left transition-colors ${active ? 'bg-muted' : 'hover:bg-muted/60'}`}
              >
                <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0 ${st.color}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium">{st.labelKey ? t(st.labelKey) : st.label}</span>
                    {active && <Check className="h-3.5 w-3.5 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{st.descKey ? t(st.descKey) : st.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-1.5 border-t border-border">
          <button
            data-testid="set-type-delete"
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center bg-red-500/15 flex-shrink-0">
              <Trash2 className="h-3.5 w-3.5" />
            </div>
            <span className="text-sm font-medium">{t('workout.deleteSet')}</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
